import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
  Update,
} from '@reduxjs/toolkit'
import { StoreData } from '../reducers/typings'
import { ILeadOppProduct, ILeadOppProductDeleteModel } from './typings'
import {
  getLeadOppProducts,
  createLeadOppProduct,
  deleteLeadOppProduct,
  updateLeadOppProduct,
  reorderLeadOppProduct,
} from './api'

const leadOppProductAdapter = createEntityAdapter<ILeadOppProduct>()

export const initialState = leadOppProductAdapter.getInitialState()

const leadOppProducts = createSlice({
  name: 'leadOppProducts',
  initialState,
  reducers: {
    addLeadOppProduct: (state, { payload }: PayloadAction<ILeadOppProduct>) => {
      leadOppProductAdapter.addOne(state, { ...payload, id: state.ids.length })
    },
    removeLeadOppProduct: (
      state,
      { payload }: PayloadAction<ILeadOppProductDeleteModel>,
    ) => {
      leadOppProductAdapter.removeOne(state, payload.id)
    },
    removeLeadOppProducts: (state, { payload }: PayloadAction<number[]>) => {
      leadOppProductAdapter.removeMany(state, payload)
    },
    resetLeadOppProducts: () => initialState,
    editLeadOppProduct: (
      state,
      { payload }: PayloadAction<ILeadOppProduct>,
    ) => {
      leadOppProductAdapter.upsertOne(state, payload)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getLeadOppProducts.fulfilled, (state, { payload }) => {
      leadOppProductAdapter.setAll(state, payload)
    })
    builder.addCase(getLeadOppProducts.rejected, (state) => {
      leadOppProductAdapter.removeAll(state)
    })
    builder.addCase(createLeadOppProduct.fulfilled, (state, { payload }) => {
      leadOppProductAdapter.addOne(state, payload)
    })
    builder.addCase(updateLeadOppProduct.fulfilled, (state, { payload }) => {
      leadOppProductAdapter.upsertOne(state, payload)
    })
    builder.addCase(deleteLeadOppProduct.fulfilled, (state, { payload }) => {
      leadOppProductAdapter.removeOne(state, payload.id)
    })
    builder.addCase(reorderLeadOppProduct.fulfilled, (state, { payload }) => {
      const update: Update<ILeadOppProduct>[] =
        payload.leadOppProductOrders.map((s) => ({
          id: s.id,
          changes: { sortOrder: s.order },
        }))

      leadOppProductAdapter.updateMany(state, update)
    })
  },
})

export default leadOppProducts.reducer

export const {
  addLeadOppProduct,
  removeLeadOppProduct,
  removeLeadOppProducts,
  editLeadOppProduct,
  resetLeadOppProducts,
} = leadOppProducts.actions

const selectors = leadOppProductAdapter.getSelectors<StoreData>(
  (state) => state.leadOppProducts,
)

export const selectAllLeadOppProducts = (state) =>
  selectors.selectAll(state).sort((a, b) => a.sortOrder - b.sortOrder)
